/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import * as React from 'react';
import styled from 'styled-components';
import { Fragment, memo } from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import { ReorderableSeparator, ReorderableItem } from '../../../reorderable';
import { useStory } from '../../../../app';
import { LAYER_HEIGHT } from './constants';
import ElementLayer from './elementLayer';
import type { Element } from '@src/web-stories-wp/story-editor/src/elements/types';

const LayerSeparator = styled(ReorderableSeparator)`
  height: ${LAYER_HEIGHT}px;
  margin-block: -${LAYER_HEIGHT / 2}px;
  padding-block: ${LAYER_HEIGHT / 2}px;
`;

export const ReorderableElementLayer = memo(function ReorderableLayer({
  id,
  canPlaceElementsOnTopOfLayer = false,
  reorderingGroupId,
  isFirstLayerAfterGroup = false,
  nestedOffsetCalcFunc,
  handleStartReordering,
  previousElementGroupId,
  position,
}: {
  id: string;
  canPlaceElementsOnTopOfLayer: boolean;
  reorderingGroupId?: string;
  isFirstLayerAfterGroup: boolean;
  nestedOffsetCalcFunc: (evt) => number;
  handleStartReordering: (element: Element) => void;
  previousElementGroupId?: string;
  position: number;
}) {
  const { element, elementsInReorderingGroup, groups } = useStory(
    ({ state }) => ({
      element: state.currentPage?.elements.find((el) => el.id === id),
      elementsInReorderingGroup: reorderingGroupId
        ? state.currentPage?.elements.filter(
            (el) => el.groupId === reorderingGroupId
          )
        : [],
      groups: state.currentPage?.groups || {},
    })
  );

  const groupId = element.groupId || previousElementGroupId;
  // If we are reordering a group, but it only has one element, the behaviour should be the same
  // as if the element was not in a group.
  const isReorderingGroup = elementsInReorderingGroup.length > 1;

  const group = groups[groupId];

  return element ? (
    <Fragment key={id}>
      {/* This separator is used to move an element after the previous group and before the current element */}
      {isFirstLayerAfterGroup || canPlaceElementsOnTopOfLayer ? (
        <LayerSeparator position={position + 1} />
      ) : null}
      {/* This one is used to move an element inside the current or previous group, even if the current element is not inside the group (ie if it's just after the last group element) */}
      {!group || group.isCollapsed || isReorderingGroup ? null : (
        <LayerSeparator
          groupId={groupId}
          nestedOffset={isFirstLayerAfterGroup}
          nestedOffsetCalcFunc={nestedOffsetCalcFunc}
          position={position + 1}
          isNested={groupId}
        />
      )}
      <ReorderableItem
        position={position}
        onStartReordering={handleStartReordering(element)}
        disabled={element.isBackground}
      >
        <ElementLayer layer={element} />
      </ReorderableItem>
    </Fragment>
  ) : null;
});
